import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addDays,
  dayCounts,
  isoDay,
  monthGrid,
  sameDay,
  startOfDay,
} from "@/lib/meeting-calendar";
import { useNow } from "@/lib/use-now";
import { useMeetingCalendar } from "./useMeetingCalendar";

// TES-1233 — Emelie: "Månadsvyn ger kapacitetsöverblick med antal bokade
// möten och lediga tider per dag. Klick på en dag öppnar den dagen i
// sjudagarsvyn."
//
// "Lediga tider" are the bookable slots (BookingService.listSlots), which
// start tomorrow and reach as far as the booking horizon; a day beyond it
// shows none because none can be booked yet, not because the schedule is
// empty.
const WEEKDAYS = ["mån", "tis", "ons", "tor", "fre", "lör", "sön"];

export function MonthCalendar({
  anchor,
  onAnchor,
  onOpenDay,
  onUnauthorized,
}: {
  anchor: Date;
  onAnchor: (day: Date) => void;
  onOpenDay: (day: Date) => void;
  onUnauthorized: () => void;
}) {
  const now = useNow(60_000);
  const month = useMemo(
    () => new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    [anchor],
  );
  const grid = useMemo(() => monthGrid(month), [month]);
  const from = grid[0]!;
  const to = useMemo(() => addDays(grid[grid.length - 1]!, 1), [grid]);
  const { data, error } = useMeetingCalendar(from, to, onUnauthorized);
  const today = startOfDay(new Date(now));
  const title = month.toLocaleDateString("sv-SE", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex flex-wrap items-center gap-sm">
        <button
          type="button"
          onClick={() => onAnchor(startOfDay(new Date()))}
          className="rounded-md border border-surfaceMuted px-md py-xs text-sm hover:border-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          I dag
        </button>
        <div className="flex items-center">
          <button
            type="button"
            onClick={() =>
              onAnchor(new Date(month.getFullYear(), month.getMonth() - 1, 1))
            }
            className="rounded-md p-xs hover:bg-surfaceMuted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
            <span className="sr-only">Föregående månad</span>
          </button>
          <button
            type="button"
            onClick={() =>
              onAnchor(new Date(month.getFullYear(), month.getMonth() + 1, 1))
            }
            className="rounded-md p-xs hover:bg-surfaceMuted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <ChevronRight aria-hidden className="h-5 w-5" />
            <span className="sr-only">Nästa månad</span>
          </button>
        </div>
        <h2 className="font-display text-lg capitalize" aria-live="polite">
          {title}
        </h2>
        {!data && !error ? (
          <span className="text-sm text-textSecondary">Hämtar…</span>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="rounded-md border border-danger/30 bg-danger/10 p-md text-sm text-danger"
        >
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <div className="grid min-w-[36rem] grid-cols-7 gap-[2px] rounded-md border border-surfaceMuted bg-surfaceMuted p-[2px]">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              aria-hidden
              className="bg-surface px-xs py-xs text-center text-xs uppercase tracking-wide text-textSecondary"
            >
              {w}
            </div>
          ))}
          {grid.map((d) => {
            const inMonth = d.getMonth() === month.getMonth();
            const isToday = sameDay(d, today);
            const { booked, free } = dayCounts(data, d);
            const dateLabel = d.toLocaleDateString("sv-SE", {
              weekday: "long",
              day: "numeric",
              month: "long",
            });
            return (
              <button
                key={isoDay(d)}
                type="button"
                onClick={() => onOpenDay(d)}
                aria-current={isToday ? "date" : undefined}
                aria-label={`${dateLabel}: ${booked} bokade, ${free} lediga. Öppna i 7 dagar.`}
                className={`flex min-h-[5.5rem] flex-col items-start gap-xs p-xs text-left text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                  inMonth ? "bg-surface" : "bg-surface/60 text-textTertiary"
                } ${isToday ? "ring-2 ring-inset ring-brand" : ""} hover:bg-brand/5`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-sm ${
                    isToday ? "bg-brand font-semibold text-textOnBrand" : ""
                  }`}
                >
                  {d.getDate()}
                </span>
                {booked > 0 ? (
                  <span className="rounded-sm bg-brand px-xs text-textOnBrand">
                    {booked} bokade
                  </span>
                ) : null}
                {free > 0 ? (
                  <span className="rounded-sm border border-brand/30 bg-brand/15 px-xs text-brand-deep">
                    {free} lediga
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
